import { getStartOfDayWithTimezone } from "@redotech/util/date";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import { FulfillmentGroupColumnKey } from "../../fulfillments/fulfillment-group-column-keys";
import { SortDirection } from "../../tables/table";
import { ArrayStringFilterOperator } from "../advanced-filters/array-string-filter";
import { BooleanFilterOperator } from "../advanced-filters/boolean-filter";
import {
  DateFilterOperator,
  KnownDateFilterTimeFrame,
  RelativeTimeUnit,
} from "../advanced-filters/date-filter";
import { ExistsFilterOperator } from "../advanced-filters/exists-filter";
import {
  AdvancedFilterType,
  FilterOperator,
} from "../advanced-filters/generic-advanced-filter-data";
import { NumberFilterOperator } from "../advanced-filters/number-filter";
import { StringFilterOperator } from "../advanced-filters/string-filter";
import {
  AdvancedFilterData,
  AdvancedFilterDataSchema,
  FilterGroup,
} from "../views";

const AllFilterOperators = [
  ...Object.values(ArrayStringFilterOperator),
  ...Object.values(DateFilterOperator),
  ...Object.values(NumberFilterOperator),
  ...Object.values(BooleanFilterOperator),
  ...Object.values(ExistsFilterOperator),
  ...Object.values(StringFilterOperator),
] as const;

/**
 * The timeframes an agent can use for date filters.
 * Excludes CUSTOM (requires date picker) and RELATIVE (use relativeDateValue/Unit instead).
 */
const AgentDateTimeframes = [
  KnownDateFilterTimeFrame.TODAY,
  KnownDateFilterTimeFrame.THIS_WEEK,
  KnownDateFilterTimeFrame.LAST_WEEK,
  KnownDateFilterTimeFrame.THIS_MONTH,
  KnownDateFilterTimeFrame.LAST_MONTH,
  KnownDateFilterTimeFrame.THIS_YEAR,
  KnownDateFilterTimeFrame.LAST_YEAR,
] as const;

const agentFilterSchema = z.object({
  name: z.string().describe("Filter field name from get-filter-details"),
  operator: z
    .enum(AllFilterOperators)
    .describe("Operator from get-filter-details"),

  // array_to_string filters
  arrayValues: z
    .array(z.string())
    .nullable()
    .describe(
      "For array/string filters: the exact values to match. Null for other filter types.",
    ),

  // date filters — known timeframe
  dateTimeframe: z
    .enum(AgentDateTimeframes)
    .nullable()
    .describe(
      "For date filters: a known period. Null for other filter types or relative dates.",
    ),

  // date filters — relative
  relativeDateValue: z
    .number()
    .positive()
    .nullable()
    .describe(
      "For relative date filters: the number of time units. Null for other filter types.",
    ),
  relativeDateUnit: createEnum(RelativeTimeUnit, {
    enumName: "RelativeTimeUnit",
  })
    .nullable()
    .describe(
      "For relative date filters: the time unit. Null for other filter types.",
    ),

  // date filters — custom specific dates
  customDateStart: z
    .string()
    .nullable()
    .describe(
      "For custom date filters: the date as YYYY-MM-DD (e.g., '2026-03-01'). " +
        "Used with 'before' (single date), 'after' (single date), or 'within' (start of range). " +
        "The timezone conversion is handled automatically. Null for other filter types.",
    ),
  customDateEnd: z
    .string()
    .nullable()
    .describe(
      "For custom date 'within' operator: the end date as YYYY-MM-DD (e.g., '2026-03-31'). " +
        "Only needed for date ranges with 'within'. Null otherwise.",
    ),

  // number filters
  numberValues: z
    .array(z.number())
    .nullable()
    .describe(
      "For number filters: single value [100] or range [min, max] for BETWEEN. Null for other filter types.",
    ),

  // boolean filters
  booleanValue: z
    .boolean()
    .nullable()
    .describe(
      "For boolean filters: true or false. Null for other filter types.",
    ),
});

export const AgentFilterSchema = agentFilterSchema;
export type AgentFilter = z.infer<typeof agentFilterSchema>;

export function buildAdvancedFilterData(
  filter: AgentFilter,
  filterType: AdvancedFilterType,
  merchantTimezone?: string,
): AdvancedFilterData {
  const base = { name: filter.name, operator: filter.operator };
  const tz = merchantTimezone ?? "America/Denver";

  let raw: Record<string, unknown>;

  switch (filterType) {
    case AdvancedFilterType.ARRAY_TO_STRING:
      raw = {
        ...base,
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: filter.arrayValues ?? [],
      };
      break;

    case AdvancedFilterType.DATE: {
      if (filter.relativeDateValue != null && filter.relativeDateUnit != null) {
        raw = {
          ...base,
          type: AdvancedFilterType.DATE,
          value: KnownDateFilterTimeFrame.RELATIVE,
          relativeTime: {
            value: filter.relativeDateValue,
            unit: filter.relativeDateUnit,
          },
        };
      } else if (
        filter.customDateStart != null &&
        filter.customDateEnd != null
      ) {
        raw = {
          ...base,
          type: AdvancedFilterType.DATE,
          value: KnownDateFilterTimeFrame.CUSTOM,
          customDate: [
            getStartOfDayWithTimezone(
              Temporal.PlainDate.from(filter.customDateStart),
              tz,
            ),
            getStartOfDayWithTimezone(
              Temporal.PlainDate.from(filter.customDateEnd),
              tz,
            ),
          ],
        };
      } else if (filter.customDateStart != null) {
        raw = {
          ...base,
          type: AdvancedFilterType.DATE,
          value: KnownDateFilterTimeFrame.CUSTOM,
          customDate: [
            getStartOfDayWithTimezone(
              Temporal.PlainDate.from(filter.customDateStart),
              tz,
            ),
          ],
        };
      } else {
        raw = {
          ...base,
          type: AdvancedFilterType.DATE,
          value: filter.dateTimeframe ?? null,
        };
      }
      break;
    }

    case AdvancedFilterType.NUMBER:
      raw = {
        ...base,
        type: AdvancedFilterType.NUMBER,
        value: filter.numberValues ?? null,
      };
      break;

    case AdvancedFilterType.BOOLEAN:
      raw = {
        ...base,
        type: AdvancedFilterType.BOOLEAN,
        value: filter.booleanValue ?? null,
      };
      break;

    case AdvancedFilterType.EXISTS:
      raw = { ...base, type: AdvancedFilterType.EXISTS, value: null };
      break;

    case AdvancedFilterType.STRING:
      raw = {
        ...base,
        type: AdvancedFilterType.STRING,
        value: filter.arrayValues ?? [],
      };
      break;
  }

  const result = AdvancedFilterDataSchema.parse(raw);
  return result;
}

export function buildFilterGroup(
  filters: AgentFilter[],
  filterTypes: Map<string, AdvancedFilterType>,
  merchantTimezone?: string,
): FilterGroup {
  const clauses = filters.map((f) => {
    const type = filterTypes.get(f.name);
    if (!type) {
      throw new Error(`Unknown filter name: "${f.name}"`);
    }
    return buildAdvancedFilterData(f, type, merchantTimezone);
  });

  return { operator: FilterOperator.AND, clauses };
}

// Value format descriptions per filter type — used by FilterCatalogEntry.valueFormat

export const VALUE_FORMAT_ARRAY_TO_STRING =
  "Use arrayValues with the exact string values. Get valid values from get-filter-values (for dynamic filters) or use staticValues directly.";

const dateTimeframeList = AgentDateTimeframes.map((t) => `'${t}'`).join(", ");
const relativeUnitList = Object.values(RelativeTimeUnit)
  .map((u) => `'${u}'`)
  .join(", ");

export const VALUE_FORMAT_DATE =
  `Use dateTimeframe for known periods: ${dateTimeframeList}. ` +
  `For rolling windows, use relativeDateValue (positive number) + relativeDateUnit (${relativeUnitList}). ` +
  "For specific dates, use customDateStart (YYYY-MM-DD) with 'before' or 'after' operators, " +
  "or customDateStart + customDateEnd for a 'within' date range. Timezone conversion is handled automatically. " +
  "Example: last 5 days → relativeDateValue: 5, relativeDateUnit: 'days'. " +
  "Example: before March 1 → operator: 'before', customDateStart: '2026-03-01'. " +
  "Example: orders in February → operator: 'within', customDateStart: '2026-02-01', customDateEnd: '2026-02-28'.";

export const VALUE_FORMAT_NUMBER =
  "Use numberValues as an array: single value [100] for EQUAL/LESS_THAN/GREATER_THAN, or two values [10, 100] for BETWEEN.";

export const VALUE_FORMAT_BOOLEAN = "Use booleanValue: true or false.";

export const VALUE_FORMAT_EXISTS =
  "No value fields needed — the operator alone determines the filter.";

export const AgentColumnDefinitionSchema = z.object({
  key: createEnum(FulfillmentGroupColumnKey).describe(
    "The column key (from get-column-catalog or get-column-details)",
  ),
  width: z
    .number()
    .describe(
      "Column width in pixels. Use 0 to use the column's default width from the catalog.",
    ),
});
export type AgentColumnDefinition = z.infer<typeof AgentColumnDefinitionSchema>;

export const AgentSortSchema = z.object({
  key: createEnum(FulfillmentGroupColumnKey).describe(
    "The column key to sort by (must be a sortable column — check sortDirection in get-column-details).",
  ),
  direction: createEnum(SortDirection, { enumName: "SortDirection" }).describe(
    "Sort direction: 'asc' (oldest/smallest first) or 'desc' (newest/largest first).",
  ),
});
export type AgentSort = z.infer<typeof AgentSortSchema>;
